import { Link, useNavigate } from "react-router-dom";
import { Kicker } from "@/components/ui/kicker";
import { AriaGridList, AriaGridListItem } from "@/components/ui/aria-gridlist";
import { WalkthroughItemContent } from "@/components/experience/walkthrough-item";
import { RunTrend } from "@/components/experience/run-trend";
import { numberRuns, runTime } from "@/lib/run-trend";
import type { WalkthroughRow } from "./panel-types";

interface WalkthroughsBlockProps {
  walkthroughs: WalkthroughRow[];
  scenarioName: string;
  projectId: string;
}

export function WalkthroughsBlock({
  walkthroughs,
  scenarioName,
  projectId,
}: WalkthroughsBlockProps) {
  const navigate = useNavigate();
  const sorted = [...walkthroughs].sort((a, b) => runTime(b) - runTime(a));
  const runNumbers = numberRuns(walkthroughs);
  const recent = sorted.slice(0, 8);
  const hasMore = sorted.length > recent.length;

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <Kicker as="h3">Walkthroughs</Kicker>
        <span className="text-xs tabular-nums text-muted-foreground">
          {walkthroughs.length} run{walkthroughs.length !== 1 ? "s" : ""}
        </span>
      </div>
      <RunTrend
        runs={walkthroughs}
        runNumbers={runNumbers}
        projectId={projectId}
      />
      <AriaGridList
        aria-label={`Walkthroughs for ${scenarioName}`}
        onAction={(key) =>
          navigate(`/projects/${projectId}/walkthroughs/${key}`)
        }
      >
        {recent.map((w) => (
          <AriaGridListItem
            key={w.id}
            id={w.id}
            textValue={`Run #${runNumbers.get(w.id)}`}
          >
            <WalkthroughItemContent
              walkthrough={{
                id: w.id,
                scenarioName,
                personaName: null,
                mode: w.mode,
                status: w.status,
                verdict: w.verdict as Array<{
                  rating: string;
                  reasoning?: string;
                }> | null,
                findingCount: w.findingCount,
                severityCounts: w.severityCounts,
                completedAt: w.completedAt,
                startedAt: w.startedAt,
                createdAt: w.createdAt,
              }}
              label={`Run #${runNumbers.get(w.id)}`}
              showPersona={false}
            />
          </AriaGridListItem>
        ))}
      </AriaGridList>
      {hasMore && (
        <Link
          to={`/projects/${projectId}/walkthroughs`}
          className="inline-block text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
        >
          See all {walkthroughs.length} walkthroughs →
        </Link>
      )}
    </section>
  );
}
